import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useImmer } from "use-immer";
import {
  BreadcrumbsProviderType,
  isOverviewBreadcrum,
  Breadcrumb,
  Breadcrumbs,
  RecreateBreadcrumbParams,
} from "./types";
import { getBreadcrumbsIdsToRemove } from "./utils";

const BreadcrumbsContext = createContext<BreadcrumbsProviderType>({
  breadcrumbs: {},
  receiveBreadcrumb() {
    //
  },
  toggleBreadcrumb() {
    //
  },
  recreateBreadcrumbs() {
    //
  },
});

BreadcrumbsContext.displayName = "Context -> Breadcrumbs";

type Props = {
  children: ReactNode;
};

const BreadcrumbsContextProvider = ({ children }: Props) => {
  const [breadcrumbs, setBreadcrumbs] = useImmer<Breadcrumbs>({});

  const receiveBreadcrumb = useCallback(
    (breadcrumb: Breadcrumb) => {
      // if its overview then reset breadcrumbs and set new base
      if (isOverviewBreadcrum(breadcrumb)) {
        setBreadcrumbs((_draft) => ({
          [breadcrumb.id]: breadcrumb,
        }));
        return;
      }

      // add new breadcrumb
      setBreadcrumbs((draft) => {
        draft[breadcrumb.id] = breadcrumb;
      });
    },
    [setBreadcrumbs]
  );

  const toggleBreadcrumb = useCallback(
    (breadcrumbId: number) => {
      const idsToRemove = getBreadcrumbsIdsToRemove(breadcrumbId, breadcrumbs);
      setBreadcrumbs((draft) => {
        idsToRemove.forEach((idToRemove) => {
          delete draft[idToRemove];
        });
        // all child breadcrumbs were removed
        draft[breadcrumbId].childId = null;
      });
    },
    [setBreadcrumbs, breadcrumbs]
  );

  const recreateBreadcrumbs = useCallback(
    ({
      overviewId,
      detailId,
      moduleId,
      rowKeyValue,
      sor,
      detailType,
    }: RecreateBreadcrumbParams) => {
      if (overviewId === detailId) {
        setBreadcrumbs({
          [overviewId]: {
            parentId: null,
            childId: detailId,
            id: overviewId,
            type: "overview",
            defaultSOR: sor,
            moduleId,
          },
        });
      } else {
        setBreadcrumbs({
          [overviewId]: {
            parentId: null,
            childId: detailId,
            id: overviewId,
            type: "overview",
            defaultSOR: sor,
            moduleId,
          },
          [detailId]: {
            parentId: overviewId,
            childId: null,
            id: detailId,
            type: detailType,
            rowKey: rowKeyValue,
            defaultSOR: sor,
            overviewId,
          },
        });
      }
    },
    [setBreadcrumbs]
  );

  const value = useMemo(
    () => ({
      breadcrumbs,
      receiveBreadcrumb,
      toggleBreadcrumb,
      recreateBreadcrumbs,
    }),
    [breadcrumbs, receiveBreadcrumb, toggleBreadcrumb, recreateBreadcrumbs]
  );

  return (
    <BreadcrumbsContext.Provider value={value}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const useBreadcrumbsContext = () => {
  const context = useContext(BreadcrumbsContext);

  if (!context) {
    throw new Error(
      "You cant use context data outside of BreadcrumbsContext providet"
    );
  }

  return context;
};

export default BreadcrumbsContextProvider;
